import type { BONUSX_SUBSCRIPTION_TIERS } from "../../../../../services/user-subscription/enums/bonusx-subscription-tiers.enum";
import type { IChatbotMessage } from "../../../entities/chatbot-message.entity";
import { AI_CHATBOT_ACTIONS, type AllChatbotActions } from "../../reducer/actions/actions";
import type { IChatbotDispatcherService } from "./chatbot.dispatcher.service.interface";

export class ChatbotDispatcherService implements IChatbotDispatcherService {
  constructor(private readonly _dispatch: React.Dispatch<AllChatbotActions>) {}

  public setStartStreamingResponse(): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_IS_STREAMING_RESPONSE, payload: { isStreamingResponse: true } });
  }

  public setEndStreamingResponse(): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_IS_STREAMING_RESPONSE, payload: { isStreamingResponse: false } });
  }

  public setScrollToBottomAction(value: boolean): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_SCROLL_TO_BOTTOM_ACTION, payload: { scrollToBottom: value } });
  }

  public showScrollToBottomButton(): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_SHOW_SCROLL_TO_BOTTOM_BUTTON_ACTION,
      payload: { showScrollToBottomButton: true },
    });
  }

  public hideScrollToBottomButton(): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_SHOW_SCROLL_TO_BOTTOM_BUTTON_ACTION,
      payload: { showScrollToBottomButton: false },
    });
  }

  public appendUserMessage(userMessage: IChatbotMessage): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.ADD_USER_MESSAGE_ACTION,
      payload: { message: userMessage },
    });
  }

  public setShowEmptyState(value: boolean): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_SHOW_EMPTY_STATE_ACTION, payload: { showEmptyState: value } });
  }

  public setIsSendingAction(value: boolean): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_IS_SENDING_ACTION, payload: { isSending: value } });
  }

  public appendAiMessage(aiMessage: IChatbotMessage): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.ADD_AI_RESPONSE_ACTION, payload: { message: aiMessage } });
  }

  public appendAiMessageChunk(aiMessageChunk: IChatbotMessage): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.ADD_AI_RESPONSE_STREAM_ACTION, payload: { message: aiMessageChunk } });
  }

  public setIsCreatingNewSession(value: boolean): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.SET_IS_CREATING_NEW_SESSION, payload: { isCreatingNewSession: value } });
  }

  public clearChatMessages(): void {
    this._dispatch({ type: AI_CHATBOT_ACTIONS.CLEAR_CHAT_MESSAGES });
  }

  public setCanCloneFromPreviousChat(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_CAN_CLONE_FROM_PREVIOUS_CHAT,
      payload: { canCloneFromPreviousChat: value },
    });
  }

  public setIsCloningFromPreviousChat(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_IS_CLONING_FROM_PREVIOUS_CHAT,
      payload: { isCloningFromPreviousChat: value },
    });
  }

  public setIsAvailableInCurrentSubscription(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_IS_AVAILABLE_IN_CURRENT_SUBSCRIPTION,
      payload: { isAvailableInCurrentSubscription: value },
    });
  }

  public setUserSubscriptionTier(subscriptionTier: BONUSX_SUBSCRIPTION_TIERS): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_USER_SUBSCRIPTION_TIER,
      payload: { userSubscriptionTier: subscriptionTier },
    });
  }

  public populateChatMessages(chatMessages: IChatbotMessage[]): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.POPULATE_CHAT_MESSAGES,
      payload: { messages: chatMessages },
    });
  }

  public setErrorWhileGeneratingResponse(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_ERROR_WHILE_GENERATING_RESPONSE,
      payload: { errorWhileGeneratingResponse: value },
    });
  }

  public setRequiredTierForChatbot(tier: string): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_REQUIRED_TIER_FOR_CHATBOT,
      payload: { requiredTierForChatbot: tier },
    });
  }

  public setRateLimitExceeded(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_RATE_LIMIT_EXCEEDED,
      payload: { rateLimitExceeded: value },
    });
  }

  public setErrorMessage(message: string | undefined): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_ERROR_MESSAGE,
      payload: { errorMessage: message },
    });
  }

  public setShowRetryButton(value: boolean): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_SHOW_RETRY_BUTTON,
      payload: { showRetryButton: value },
    });
  }

  public setRetryButtonText(text: string | undefined): void {
    this._dispatch({
      type: AI_CHATBOT_ACTIONS.SET_RETRY_BUTTON_TEXT,
      payload: { retryButtonText: text },
    });
  }
}
